
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action } from "vuex-class"

import Guid from "@/scripts/guid"

import User, { LabeledValue } from "@/types/user"
import { CaseDetails, Team, dataTypeByFieldType, DataType, Field, CaseField } from "@/types"
import { caseColumns, CaseColumn, usersPlusDynamic } from "@/types/caseColumns"
import { InsightSearch, Operator, InsightCalculation, operatorLabels, operatorsByFieldType } from "@/types/insights"
import { dateRangeLabels, dateRangeRangeLabels } from "@/types/insights/dateRange"
import { DateClauseType } from "@/types/insights/dateClauseType"

import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"

import DateTime from "@/components/Fields/DateTime.vue"
import Numeric from "@/components/Fields/Numeric.vue"
import UserRole from "@/components/CaseDetails/Users/UserRole.vue"
import MapSearchBounds from "./MapSearchBounds.vue"
import DateTimeParameterValue from "./DateTimeParameterValue.vue"

import api from "@/services/api"
import { getDefaultSearchClauseParameters, Clause, SearchClauseParameters, DayOfWeek } from "@/types/view"
import { DynamicValue } from "@/types/dynamic"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import positionDropdown from "@/scripts/VSelectDropdownPosition.ts"
import { parse } from "path"
import { watch } from "fs"

const setup = namespace("setup")

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		datetime: DateTime,
		numeric: Numeric,
		"map-search": MapSearchBounds,
		"dataSourceSelect": DataSourceSelect,
		"date-time-param": DateTimeParameterValue,
		"user-role": UserRole
	}
})
export default class ParameterValue extends Vue {
	@Prop({ default: null })
	field!: CaseColumn;

	@Prop({ default: () => [] })
	allCaseFields!: CaseColumn[];

	@Prop({ default: null })
	value!: string | null;

	/** A list of currently selected field and values. Used to filter sub categories based on category selected. */
	@Prop({ type: Object, default: () => ({ }) })
	selectedFields!: Record<string, LabeledValue>;

	@Prop({ default: false })
	isNewClause!: boolean;

	@Prop({ default: () => Guid.newGuid() })
	clauseIndex!: number | string;

	@Prop({ default: null })
	operator!: Operator | null;

	@Prop({ default: true })
	dynamicValue!: boolean;

	@Prop({ default: true })
	usersAllowDynamic!: boolean;

	@Prop({ default: false })
	userRolesAllowTeams!: boolean;

	@Prop({ default: () => null })
	searchClauseParameters!: SearchClauseParameters;

	@Prop({ default: false })
	showCollapsed!: boolean

	@Prop({ default: false })
	setFieldValue!: boolean

	@Prop({ default: false })
	allowMultiple!: boolean

	@Prop({ default: true })
	allowDynamic!: boolean

	@setup.State
	fields!: Field[];

	@setup.Action loadFields!: () => Promise<any[]>;

	@Action("loadTenantSettings")
	private loadTenantSettings!: () => Promise<void>

	private selectedSearchClauseParameters: SearchClauseParameters = this.searchClauseParameters;

	private timeSpentDataSourceId: string = "";
	private isMounted: boolean = false;

	async mounted () {
		await this.loadTenantSettings() // tech debt - do this once per page refresh or less?
		this.selectedSearchClauseParameters = this.searchClauseParameters ? this.searchClauseParameters : getDefaultSearchClauseParameters()

		await this.loadFields()

		if (this.fieldDataType === DataType.timeSpentAsValue) {
			const trackingFieldId = this.field.fieldExtensions["trackingFieldId"]
			if (this.isNewClause) {
				this.selectedSearchClauseParameters.timeSpentClause.trackingFieldId = trackingFieldId
			}

			this.timeSpentDataSourceId = this.getTrackedFieldDataSourceId(trackingFieldId)
		}

		if (this.isNewClause) {
			this.dynamicValueChanged(true)
		}

		// for some child components, the relevant props are only set in ParameterValue.vue when it has finished mounting (code directly above), so need to wait until it has fully mounted before rendering children
		this.isMounted = true
	}

	private Operator = Operator;
	private DataType = DataType;
	private DateClauseType = DateClauseType;

	private calcDropdownPosition = positionDropdown;

	private fieldValueKey: string | null = null;
	private returnedFieldOptions: any[] = [];

	private teams: Team[] = [];

	ranges: any[] = [];
	private rangeFilter: string | null = null;

	private selectedItem = null;

	private selectedDayOfWeek: string | null = null;

	/** Builds a CaseDetails record from this.selectedFields to be passed to DataSourceSelect */
	private get currentCase () {
		var allFields = this.fields

		var caseFields: {[key: string]: CaseField} = {}

		// Build up an object containing all 'selectedFields' as an object with each member being of the type [key: string]: CaseField.
		if (this.selectedFields) {
			var caseFieldsKeys = Object.keys(this.selectedFields)
			caseFieldsKeys.forEach(key => {
				var field = allFields.find(field => field.id === key)

				if (!field) { return }

				// CaseField
				caseFields[key] = {
					oldValue: null,
					value: this.selectedFields[key].value,
					label: this.selectedFields[key].label,
					valueObject: null,
					section: "",
					order: 0,
					field: field,
					requiredCase: false,
					requiredRecord: false
				}
			})
		}

		// Build up a blank CaseDetails with just the fields member.
		var caseDetails: CaseDetails = {
			id: "",
			caseNumber: "",
			title: "",
			statusId: "",
			severityId: "",
			templateId: null,
			templateTitle: null,
			assigneeId: null,
			openedByUserId: null,
			isCase: false,
			isClosed: false,
			isSensitive: false,
			lastUpdatedAt: null,
			categoryTitle: null,
			statusTitle: "",
			severityTitle: "",
			severityLevel: 0,
			openedByUser: null,
			areas: [],
			summary: "",
			fields: caseFields,
			sections: [],
			sectionDetails: undefined,
			links: [],
			actions: [],
			permissions: null,
			tags: null,
			users: [],
			cadenceDetails: null,
			scheduling: undefined,
			builtInFieldLocks: null,
			pendingTeams: [],
			viewingUsers: [],
			cadenceEnabled: false,
			hiddenFromInsights: false
		}

		return caseDetails
	}

	private get fieldAsFieldType () {
		var field = this.fields.find(f => f.id === this.field.key)

		if (field) { return field } else { return undefined }
	}

	public get fieldDataType (): DataType | null {
		if (!this.field) return null

		return this.field.comparisonType !== undefined
			? this.field.comparisonType
			: this.field.fieldDataType
				? this.field.fieldDataType
				: null
	}

	private get daysOfWeekNames (): string[] {
		return Object.values(DayOfWeek).filter(value => typeof value === "string") as string[]
	}

	private get dynamicInputValue (): DynamicValue | null {
		return this.value !== null
			? {
				value: this.value,
				dynamic: this.dynamicValue
			}
			: null
	}

	private get dateTimeText (): string {
		if (!this.field || this.value === null) return "Select value"

		if (this.dynamicValue) {
			const dynamicInt = parseInt(this.value)
			if (isNaN(dynamicInt)) {
				if (this.operator === Operator.DateRange) {
					if (this.value === "WeekNumber") {
						return "Week #" + (this.selectedSearchClauseParameters && this.selectedSearchClauseParameters.weekNumber
							? this.selectedSearchClauseParameters.weekNumber
							: "")
					} else if (this.value === "DateRange" && this.selectedSearchClauseParameters) {
						return "From " +
							(this.selectedSearchClauseParameters.startDate ? formatDateMixin.methods.displayDateOnlyFromISOString(this.selectedSearchClauseParameters.startDate!) : "") +
							" to " + (this.selectedSearchClauseParameters.endDate ? formatDateMixin.methods.displayDateOnlyFromISOString(this.selectedSearchClauseParameters.endDate!) : "")
					}
					return dateRangeRangeLabels.findAndGet(d => d.id === this.value, d => d.title, "")
				} else {
					return dateRangeLabels.findAndGet(d => d.id === this.value, d => `Start of ${d.title}`, "")
				}
			} else if (dynamicInt === 0) {
				return "Today"
			} else if (dynamicInt > 0) {
				return dynamicInt + " days from now"
			} else {
				return Math.abs(dynamicInt) + " days ago"
			}
		}

		return formatDateMixin.methods.displayDateOnlyFromISOString(new Date(this.value))
	}

	public get fieldOptions (): any[] {
		if (this.field && (this.field.dataType === "date" || this.field.dataType === "date-time" ||
			this.field.fieldDataType === DataType.dateTime)) {
			this.returnedFieldOptions = dateRangeLabels
		}
		if (this.field && this.field.columnValues && this.fieldValueKey !== this.field.key) {
			this.fieldValueKey = this.field.key
			this.returnedFieldOptions = []
			this.field.columnValues(api).then(options => this.returnedFieldOptions = options)
		}

		if (this.field && (this.field.dataType === "user-role" || this.field.dataType === "subrole")) {
			if (this.operator === Operator.InTeam) {
				this.fieldValueKey = null
				if (this.teams.length === 0) {
					this.returnedFieldOptions = []
					api.teamsGet().then(teams => this.teams = teams)
				}

				this.returnedFieldOptions = this.teams.map(t => {
					return {
						id: t.id,
						name: t.title
					}
				})
			} else if (this.fieldValueKey !== this.field.key) {
				this.fieldValueKey = this.field.key
				this.returnedFieldOptions = [];
				(this.usersAllowDynamic ? usersPlusDynamic(api) : api.usersGet()).then((options: any) => {
					if (this.userRolesAllowTeams) {
						api.teamsGet().then(teams => {
							this.returnedFieldOptions = [
								...options,
								...teams.map(t => {
									return {
										id: t.id,
										name: t.title
									}
								})
							].sortBy("title")
						})
					} else {
						this.returnedFieldOptions = options
					}
				})
			}
		}

		if (this.value && !this.selectedItem) {
			this.selectedItem = this.returnedFieldOptions.find(i => i.id === this.value)
		}

		return this.returnedFieldOptions
	}

	getTrackedFieldDataSourceId (trackedFieldId: string) : string {
		if (!this.allCaseFields) {
			Error("Missing dependent case fields")
		}

		const trackedField = this.allCaseFields.find(c => c.fieldId === trackedFieldId)
		if (!trackedField || !trackedField.dataSourceId) {
			throw Error("Missing time tracked field Data Source ID")
		}

		return trackedField!.dataSourceId!
	}

	searchClauseParametersChanged (searchClauseParameters: SearchClauseParameters) {
		this.selectedSearchClauseParameters = searchClauseParameters
		this.$emit("searchClauseParametersChanged", searchClauseParameters)
	}

	timeSpentListValueChanged (listValue: string) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.searchValue = listValue
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("input", listValue)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	timeSpentListLabelChanged (listLabel: string) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.searchLabel = listLabel
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("inputLabel", listLabel)
		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	timeSpentHoursChanged (newHoursValue: number) {
		const timeSpentClause = this.selectedSearchClauseParameters.timeSpentClause
		timeSpentClause.hoursSpent = newHoursValue
		this.$set(this.selectedSearchClauseParameters, "timeSpentClause", timeSpentClause)

		this.$emit("searchClauseParametersChanged", this.selectedSearchClauseParameters)
	}

	@Watch("value")
	ValuePropChanged (newValue: string | null) {
		if (this.selectedItem && !newValue) {
			this.selectedItem = null
		} else {
			this.selectedItem = this.returnedFieldOptions.find(i => i.id === this.value)
			if (this.selectedItem === undefined) {
				this.selectedItem = null
			}
		}
	}

	valueChanged (newValue: string | null) {
		this.$emit("input", newValue)
		this.$emit("dynamicInput", {
			value: newValue,
			dynamicValue: false
		})
	}

	valueItemChanged (newValue: any) {
		const dynamicValue = newValue && newValue.dynamicValue ? newValue.dynamicValue : false
		if (newValue && dynamicValue !== this.dynamicValue) {
			this.$emit("dynamicValueChanged", dynamicValue)
		}

		this.$emit("dynamicInput", {
			value: newValue ? newValue.id : null,
			dynamic: dynamicValue
		})

		this.$emit("input", newValue ? newValue.id : null)
		this.$emit("inputValue", newValue)

		const labelProperty = this.field.listLabel
			? this.field.listLabel
			: this.field.dataType === "user-role"
				? "name"
				: "label"
		if (newValue && newValue[labelProperty]) {
			this.$emit("inputLabel", newValue[labelProperty])
		} else {
			this.$emit("inputLabel", null)
		}

		if (this.selectedItem && !newValue) {
			this.selectedItem = null
		}
	}

	labelChanged (newLabel: string | null) {
		this.$emit("inputLabel", newLabel)
	}

	numberValueChanged (newValue: number) {
		const value = newValue === null ? null : newValue.toString()
		this.$emit("input", value)
		this.$emit("dynamicInput", {
			value: value,
			dynamic: false
		})
	}

	booleanValueChanged (newValue: boolean) {
		this.$emit("input", newValue)
	}

	dynamicValueChanged (newValue: boolean) {
		this.$emit("dynamicValueChanged", newValue === null ? false : newValue)
	}

	mapBoundsChanged (newValue: {lat: number, lng: number}[]) {
		this.$emit("input", JSON.stringify(newValue))
		this.$emit("inputLabel", "Bounds")
	}

	dynamicInputValueChanged (newValue: DynamicValue | null) {
		this.$emit("dynamicInput", newValue)

		this.$emit("dynamicValueChanged", newValue !== null && newValue.dynamic ? newValue.dynamic : false)
		this.$emit("input", newValue !== null && newValue.value !== null ? newValue!.value.toString() : null)
	}
}

